import { ExchangeRate2Response } from '@acme/api';
import { Field } from '@acme/foundation';
import { TextInput } from 'flowbite-react';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { twMerge } from 'tailwind-merge';

export type SelectProviderModalRefImperativeHandleType = {
  getValue: () => number;
};

interface SelectProviderModalProps {
  provider: ExchangeRate2Response;
  maxAmount?: number;
  initialValue?: number;
}

const defaultRangePrice = 1;

export const SelectProviderModal = forwardRef<
  SelectProviderModalRefImperativeHandleType,
  SelectProviderModalProps
>(({ provider, maxAmount, initialValue }, ref) => {
  const firstRangePrice = provider.ranges?.[0]?.price ?? defaultRangePrice;
  const [value, setValue] = useState(initialValue ?? 0);
  const [range, setRange] = useState(firstRangePrice);

  useEffect(() => {
    setRange(firstRangePrice);
  }, [firstRangePrice]);

  useEffect(() => {
    if (maxAmount != null && value > maxAmount) {
      setValue(maxAmount);
    }
  }, [maxAmount]);

  useImperativeHandle(
    ref,
    () => ({
      getValue: () => value,
    }),
    [value]
  );

  const handleRangeSelect = useCallback((price: number) => {
    setRange(price || defaultRangePrice);
  }, []);

  const handleSumChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const n = Number(e.target.value);
      if (!Number.isFinite(n) || n < 0) {
        setValue(0);
        return;
      }
      const capped = maxAmount != null ? Math.min(n, maxAmount) : n;
      setValue(capped);
    },
    [maxAmount]
  );

  const ranges = provider.ranges ?? [];
  const totalRub = useMemo(() => value * range, [value, range]);
  const totalRubDisplay = useMemo(
    () => (Number.isFinite(totalRub) ? totalRub.toFixed(2) : '0.00'),
    [totalRub]
  );

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <div className="text-gray-500 text-sm">
          Курс поставщика для разных объемов валюты
        </div>
        <div>
          {ranges.map((r) => {
            const price = r?.price ?? defaultRangePrice;
            return (
              <div
                key={`${r.min}-${r.max}`}
                role="button"
                tabIndex={0}
                onClick={() => handleRangeSelect(price)}
                onKeyDown={(e) => e.key === 'Enter' && handleRangeSelect(price)}
                className={twMerge(
                  'text-gray-500 text-sm hover:text-gray-900 cursor-pointer',
                  price === range && 'text-black'
                )}
              >
                {`от ${r.min} до ${r.max} курс: ${r.price}`}
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Field
          className="col-span-2"
          label="Название организации"
          value={provider?.name}
        />

        <Field
          className="text-brand-purple-900"
          label="Доступный объем"
          value={`${provider.amount} ${provider.cryptoCurrency}`}
        />
      </div>
      <div>
        <div className="text-sm text-gray-900 mb-2">Сумма</div>
        <TextInput
          type="number"
          min={0}
          max={maxAmount}
          value={value || ''}
          onChange={handleSumChange}
        />
      </div>
      <Field
        label="Вы заплатите с учетом курса"
        value={`${totalRubDisplay} RUB`}
      />
    </div>
  );
});
